import React, { useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import {
  Checkbox, TextField, FormControlLabel, FormGroup, FormLabel,
} from '@mui/material';
import { ClubResources } from '@alumni-ventures-group/react-shared/lib/services';
import PropTypes from 'prop-types';
import { isWhitespaceOnly } from '../shared/utils/validators';
import { DIGITAL_ASSET_QUESTION_LABELS } from './QuestionList';

const { greyColor } = ClubResources;

const styles = {
  sectionSpacing: { margin: '15px 0 5px 0' },
  questionsFontSize: '15px',
};

const digitalAssetTextInputLabels = new Map([
  [DIGITAL_ASSET_QUESTION_LABELS.digitalAssetInvestmentExplanation, 'Explanation'],
  [DIGITAL_ASSET_QUESTION_LABELS.portfolioCompanyWalletAddress, 'Wallet Address'],
]);

const digitalAssetGatingQuestionLabels = new Set([
  DIGITAL_ASSET_QUESTION_LABELS.willPurchaseDigitalAssets,
  DIGITAL_ASSET_QUESTION_LABELS.requiresDigitalAssetInvestment,
]);

const standaloneQuestionLabels = new Set([
  ...digitalAssetGatingQuestionLabels,
  DIGITAL_ASSET_QUESTION_LABELS.requiresOffshoreEntityInvestment,
]);

const getQuestion = (questions, label) => (
  questions.find((question) => question.label === label)
);

export function YesNoQuestion({
  label,
  value,
  setAnswer,
}) {
  return (
    <Row className="mt-md-3">
      <Col className="col-9">
        <FormLabel required style={{ color: 'rgb(81 84 97)' }}>{label}</FormLabel>
      </Col>
      <Col className="col-3">
        <FormGroup className="float-right" row>
          <FormControlLabel
            style={{ color: greyColor }}
            control={(
              <Checkbox
                inputProps={{ 'aria-label': `${label} yes` }}
                checked={value === true}
                onClick={() => setAnswer(true, label)}
              />
            )}
            label="Yes"
          />
          <FormControlLabel
            style={{ color: greyColor }}
            control={(
              <Checkbox
                inputProps={{ 'aria-label': `${label} no` }}
                checked={value === false}
                onClick={() => setAnswer(false, label)}
              />
            )}
            label="No"
          />
        </FormGroup>
      </Col>
    </Row>
  );
}

export function TextQuestion({
  label,
  value,
  inputLabel,
  setAnswer,
}) {
  return (
    <>
      <Row className="mt-md-3">
        <Col>
          <FormLabel required style={{ color: 'rgb(81 84 97)' }}>{label}</FormLabel>
        </Col>
      </Row>
      <Row>
        <Col>
          <TextField
            fullWidth
            value={value}
            onChange={(event) => {
              const text = event.target.value;
              if (!isWhitespaceOnly(text) && text.length <= 200) {
                setAnswer(text, label);
              }
            }}
            variant="standard"
            label={inputLabel}
            multiline
            required
            style={{
              marginBottom: '20px',
            }}
          />
        </Col>
      </Row>
    </>
  );
}

export function isQuestionActive(questionAnswers, question) {
  if (!question) {
    return false;
  }

  if (digitalAssetGatingQuestionLabels.has(question.label)) {
    return true;
  }

  const willPurchaseDigitalAssets = getQuestion(
    questionAnswers,
    DIGITAL_ASSET_QUESTION_LABELS.willPurchaseDigitalAssets,
  )?.value === true;
  if (question.label === DIGITAL_ASSET_QUESTION_LABELS.requiresOffshoreEntityInvestment) {
    return willPurchaseDigitalAssets;
  }

  const requiresDigitalAssetInvestment = getQuestion(
    questionAnswers,
    DIGITAL_ASSET_QUESTION_LABELS.requiresDigitalAssetInvestment,
  )?.value === true;

  return willPurchaseDigitalAssets && requiresDigitalAssetInvestment;
}

const isQuestionVisible = (questionAnswers, question, showStandaloneQuestions) => (
  isQuestionActive(questionAnswers, question)
  && (showStandaloneQuestions || !standaloneQuestionLabels.has(question.label))
);

export function DigitalAssetQuestionAnswers({ questions }) {
  return (
    <>
      <h5 style={styles.sectionSpacing}>Digital Asset Questions</h5>
      {questions.map((question) => {
        let displayAnswer = question.value;
        if (typeof question.value === 'boolean') {
          displayAnswer = question.value ? 'Yes' : 'No';
        }

        return (
          <div key={question.label}>
            <h5 style={{ ...styles.sectionSpacing, fontSize: styles.questionsFontSize }}>{question.label}</h5>
            <h6>&emsp;<i>Answer:</i>&ensp;“{displayAnswer}”</h6>
          </div>
        );
      })}
    </>
  );
}

export function Questions({
  title = 'Questions',
  questionAnswers,
  setAnswerList,
  setAllAnswered,
  showStandaloneQuestions,
}) {
  useEffect(() => {
    // Check if any question displayed in this flow is unanswered.
    const someUnansweredActiveQuestions = questionAnswers.some((question) => (
      isQuestionVisible(questionAnswers, question, showStandaloneQuestions)
      && (question.value === null || question.value === '')
    ));
    setAllAnswered(!someUnansweredActiveQuestions);
  }, [questionAnswers, setAllAnswered, showStandaloneQuestions]);

  const setAnswer = (value, label) => {
    const newAnswerList = questionAnswers.map((question) => (
      question.label === label ? { ...question, value } : question
    ));
    setAnswerList(newAnswerList);
  };

  const visibleQuestions = questionAnswers.filter((question) => (
    isQuestionVisible(questionAnswers, question, showStandaloneQuestions)
  ));

  if (!visibleQuestions.length) {
    return null;
  }

  return (
    <>
      <h5 className="mt-5">{title}</h5>
      {visibleQuestions.map((question) => {
        const inputLabel = digitalAssetTextInputLabels.get(question.label);
        if (!inputLabel) {
          return (
            <YesNoQuestion
              key={question.label}
              label={question.label}
              value={question.value}
              setAnswer={setAnswer}
            />
          );
        }

        return (
          <TextQuestion
            key={question.label}
            label={question.label}
            value={question.value || ''}
            inputLabel={inputLabel}
            setAnswer={setAnswer}
          />
        );
      })}
    </>
  );
}

YesNoQuestion.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.bool,
  setAnswer: PropTypes.func.isRequired,
};

TextQuestion.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  inputLabel: PropTypes.string,
  setAnswer: PropTypes.func.isRequired,
};

Questions.propTypes = {
  title: PropTypes.string,
  questionAnswers: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string,
      ]),
    }),
  ).isRequired,
  setAnswerList: PropTypes.func.isRequired,
  setAllAnswered: PropTypes.func.isRequired,
  showStandaloneQuestions: PropTypes.bool,
};

DigitalAssetQuestionAnswers.propTypes = {
  // eslint-disable-next-line react/require-default-props
  questions: Questions.propTypes.questionAnswers,
};

TextQuestion.defaultProps = {
  value: '',
  inputLabel: null,
};

YesNoQuestion.defaultProps = {
  value: null,
};

Questions.defaultProps = {
  title: 'Questions',
  showStandaloneQuestions: true,
};
